import React__default, { forwardRef } from 'react';
import _s, { css } from 'styled-components';
import useTheme from './useTheme.js';

const styled = typeof _s === "object" && "default" in _s ? _s.default : _s;
const Box = /*#__PURE__*/forwardRef(({
  alignContent,
  alignItems,
  alignSelf,
  backgroundBlendMode,
  backgroundClip,
  backgroundColor,
  backgroundImage,
  backgroundOrigin,
  backgroundPosition,
  backgroundRepeat,
  backgroundSize,
  borderBottomColor,
  borderBottomLeftRadius,
  borderBottomRightRadius,
  borderBottomStyle,
  borderBottomWidth,
  borderColor,
  borderLeftColor,
  borderLeftStyle,
  borderLeftWidth,
  borderRadius,
  borderRightColor,
  borderRightStyle,
  borderRightWidth,
  borderStyle,
  borderTopColor,
  borderTopLeftRadius,
  borderTopRightRadius,
  borderTopStyle,
  borderTopWidth,
  borderWidth,
  bottom,
  boxShadow,
  boxSizing,
  children,
  cursor,
  display,
  flex,
  flexBasis,
  flexDirection,
  flexFlow,
  flexGrow,
  flexShrink,
  flexWrap,
  height,
  id,
  justifyContent,
  justifyItems,
  justifySelf,
  left,
  margin,
  marginBottom,
  marginHorizontal,
  marginLeft,
  marginRight,
  marginTop,
  marginVertical,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  onPress,
  onKeyPress,
  opacity,
  overflow,
  overflowX,
  overflowY,
  padding,
  paddingBottom,
  paddingHorizontal,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingVertical,
  pointerEvents,
  position,
  right,
  shapeFill,
  testID,
  textAlign,
  textDecoration,
  textOverflow,
  textTransform,
  top,
  transform,
  transition,
  visibility,
  whiteSpace,
  width,
  zIndex,
  style = {}
}, ref) => {
  const [theme] = useTheme();
  const getSpacing = value => {
    if (!value) return undefined;
    return `${theme.spacings[value]}px`;
  };
  const getBorderWidth = value => {
    if (!value) return undefined;
    return `${theme.borderWidths[value]}px`;
  };
  const getBorderColor = value => {
    if (!value) return undefined;
    return theme.borderColors[value];
  };
  const getBorderRadius = value => {
    if (!value) return undefined;
    return `${theme.borderRadius[value]}px`;
  };
  const getBoxShadow = value => {
    if (!value) return undefined;
    return theme.boxShadows[value];
  };
  const getBackgroundColor = (backgroundColorValue, shapeFillValue) => {
    if (shapeFillValue) {
      return theme.shapeFills[shapeFillValue];
    }
    if (backgroundColorValue) {
      return theme.backgroundColors[backgroundColorValue];
    }
    return undefined;
  };
  return /*#__PURE__*/React__default.createElement(Container, {
    ref: ref,
    alignContent: alignContent,
    alignItems: alignItems,
    alignSelf: alignSelf,
    backgroundBlendMode: backgroundBlendMode,
    backgroundClip: backgroundClip,
    backgroundColor: getBackgroundColor(backgroundColor, shapeFill),
    backgroundImage: backgroundImage,
    backgroundOrigin: backgroundOrigin,
    backgroundPosition: backgroundPosition,
    backgroundRepeat: backgroundRepeat,
    backgroundSize: backgroundSize,
    borderBottomColor: getBorderColor(borderBottomColor),
    borderBottomLeftRadius: getBorderRadius(borderBottomLeftRadius),
    borderBottomRightRadius: getBorderRadius(borderBottomRightRadius),
    borderBottomStyle: borderBottomStyle,
    borderBottomWidth: getBorderWidth(borderBottomWidth),
    borderColor: getBorderColor(borderColor),
    borderLeftColor: getBorderColor(borderLeftColor),
    borderLeftStyle: borderLeftStyle,
    borderLeftWidth: getBorderWidth(borderLeftWidth),
    borderRadius: getBorderRadius(borderRadius),
    borderRightColor: getBorderColor(borderRightColor),
    borderRightStyle: borderRightStyle,
    borderRightWidth: getBorderWidth(borderRightWidth),
    borderStyle: borderStyle,
    borderTopColor: getBorderColor(borderTopColor),
    borderTopLeftRadius: getBorderRadius(borderTopLeftRadius),
    borderTopRightRadius: getBorderRadius(borderTopRightRadius),
    borderTopStyle: borderTopStyle,
    borderTopWidth: getBorderWidth(borderTopWidth),
    borderWidth: getBorderWidth(borderWidth),
    bottom: bottom,
    boxShadow: getBoxShadow(boxShadow),
    boxSizing: boxSizing,
    cursor: cursor,
    "data-testid": testID,
    display: display,
    flex: flex,
    flexBasis: flexBasis,
    flexDirection: flexDirection,
    flexFlow: flexFlow,
    flexGrow: flexGrow,
    flexShrink: flexShrink,
    flexWrap: flexWrap,
    height: height,
    id: id,
    justifyContent: justifyContent,
    justifyItems: justifyItems,
    justifySelf: justifySelf,
    left: left,
    margin: getSpacing(margin),
    marginBottom: getSpacing(marginBottom) || getSpacing(marginVertical),
    marginLeft: getSpacing(marginLeft) || getSpacing(marginHorizontal),
    marginRight: getSpacing(marginRight) || getSpacing(marginHorizontal),
    marginTop: getSpacing(marginTop) || getSpacing(marginVertical),
    maxHeight: maxHeight,
    maxWidth: maxWidth,
    minHeight: minHeight,
    minWidth: minWidth,
    onClick: onPress,
    onKeyPress: onKeyPress,
    opacity: opacity,
    overflow: overflow,
    overflowX: overflowX,
    overflowY: overflowY,
    padding: getSpacing(padding),
    paddingBottom: getSpacing(paddingBottom) || getSpacing(paddingVertical),
    paddingLeft: getSpacing(paddingLeft) || getSpacing(paddingHorizontal),
    paddingRight: getSpacing(paddingRight) || getSpacing(paddingHorizontal),
    paddingTop: getSpacing(paddingTop) || getSpacing(paddingVertical),
    pointerEvents: pointerEvents,
    position: position,
    right: right,
    textAlign: textAlign,
    textDecoration: textDecoration,
    textOverflow: textOverflow,
    textTransform: textTransform,
    top: top,
    transform: transform,
    transition: transition,
    visibility: visibility,
    whiteSpace: whiteSpace,
    width: width,
    zIndex: zIndex,
    style: style
  }, children);
});
const Container = styled.div`
  ${({
  alignContent,
  alignItems,
  alignSelf,
  backgroundBlendMode,
  backgroundClip,
  backgroundColor,
  backgroundImage,
  backgroundOrigin,
  backgroundPosition,
  backgroundRepeat,
  backgroundSize,
  borderBottomColor,
  borderBottomLeftRadius,
  borderBottomRightRadius,
  borderBottomStyle,
  borderBottomWidth,
  borderColor,
  borderLeftColor,
  borderLeftStyle,
  borderLeftWidth,
  borderRadius,
  borderRightColor,
  borderRightStyle,
  borderRightWidth,
  borderStyle,
  borderTopColor,
  borderTopLeftRadius,
  borderTopRightRadius,
  borderTopStyle,
  borderTopWidth,
  borderWidth,
  bottom,
  boxShadow,
  boxSizing,
  cursor,
  display,
  flex,
  flexBasis,
  flexDirection,
  flexFlow,
  flexGrow,
  flexShrink,
  flexWrap,
  height,
  justifyContent,
  justifyItems,
  justifySelf,
  left,
  margin,
  marginBottom,
  marginLeft,
  marginRight,
  marginTop,
  maxHeight,
  maxWidth,
  minHeight,
  minWidth,
  opacity,
  overflow,
  overflowX,
  overflowY,
  padding,
  paddingBottom,
  paddingLeft,
  paddingRight,
  paddingTop,
  pointerEvents,
  position,
  right,
  textAlign,
  textDecoration,
  textOverflow,
  textTransform,
  top,
  transform,
  transition,
  visibility,
  whiteSpace,
  width,
  zIndex
}) => css(["align-content:", ";align-items:", ";align-self:", ";background-blend-mode:", ";background-clip:", ";background-color:", ";background-image:", ";background-origin:", ";background-position:", ";background-repeat:", ";background-size:", ";border-bottom-color:", ";border-bottom-left-radius:", ";border-bottom-right-radius:", ";border-bottom-style:", ";border-bottom-width:", ";border-color:", ";border-left-color:", ";border-left-style:", ";border-left-width:", ";border-radius:", ";border-right-color:", ";border-right-style:", ";border-right-width:", ";border-style:", ";border-top-color:", ";border-top-left-radius:", ";border-top-right-radius:", ";border-top-style:", ";border-top-width:", ";border-width:", ";bottom:", ";box-shadow:", ";box-sizing:", ";cursor:", ";display:", ";flex-basis:", ";flex-direction:", ";flex-flow:", ";flex-grow:", ";flex-shrink:", ";flex-wrap:", ";flex:", ";height:", ";justify-content:", ";justify-items:", ";justify-self:", ";left:", ";margin-bottom:", ";margin-left:", ";margin-right:", ";margin-top:", ";margin:", ";max-height:", ";max-width:", ";min-height:", ";min-width:", ";opacity:", ";overflow-x:", ";overflow-y:", ";overflow:", ";padding-bottom:", ";padding-left:", ";padding-right:", ";padding-top:", ";padding:", ";pointer-events:", ";position:", ";right:", ";text-align:", ";text-decoration:", ";text-overflow:", ";text-transform:", ";top:", ";transform:", ";transition:", ";visibility:", ";white-space:", ";width:", ";z-index:", ";"], alignContent, alignItems, alignSelf, backgroundBlendMode, backgroundClip, backgroundColor, backgroundImage, backgroundOrigin, backgroundPosition, backgroundRepeat, backgroundSize, borderBottomColor, borderBottomLeftRadius, borderBottomRightRadius, borderBottomStyle, borderBottomWidth, borderColor, borderLeftColor, borderLeftStyle, borderLeftWidth, borderRadius, borderRightColor, borderRightStyle, borderRightWidth, borderStyle, borderTopColor, borderTopLeftRadius, borderTopRightRadius, borderTopStyle, borderTopWidth, borderWidth, bottom, boxShadow, boxSizing, cursor, display, flexBasis, flexDirection, flexFlow, flexGrow, flexShrink, flexWrap, flex, height, justifyContent, justifyItems, justifySelf, left, marginBottom, marginLeft, marginRight, marginTop, margin, maxHeight, maxWidth, minHeight, minWidth, opacity, overflowX, overflowY, overflow, paddingBottom, paddingLeft, paddingRight, paddingTop, padding, pointerEvents, position, right, textAlign, textDecoration, textOverflow, textTransform, top, transform, transition, visibility, whiteSpace, width, zIndex)}
`;

export { Box as default };
//# sourceMappingURL=Box.js.map
